import React, { useState, useMemo } from 'react'
import { styled } from 'styled-components'
import axios from "axios";
import countryList from '../../../utils/countrylist';

const BackendUrl = "https://amrs-prod.eu-north-1.elasticbeanstalk.com"

/**
 * Generic registration page for AMRS events.
 *
 * Each event's description page renders this component with its own
 * courses, API paths, and fee configuration. Nothing event-specific is
 * hard-coded here.
 *
 * ---- FEE CONFIG SHAPES ----
 *
 * Flat fee (e.g. the workshop):
 *   fee = {
 *     type: 'flat',
 *     amount: 500,
 *     currency: 'GH₵',
 *     note: 'One-time · Full 3-day access',
 *   }
 *
 * Tiered fee (e.g. the second event, by country + student status):
 *   fee = {
 *     type: 'tiered',
 *     note: 'One-time · Full access',
 *     deadline: '4th October, 2026',
 *     tiers: [
 *       { isStudent: true,  isInternational: false, amount: 500, currency: 'GH₵' },
 *       { isStudent: false, isInternational: false, amount: 750, currency: 'GH₵' },
 *       { isStudent: true,  isInternational: true,  amount: 200, currency: 'USD' },
 *       { isStudent: false, isInternational: true,  amount: 300, currency: 'USD' },
 *     ],
 *   }
 *
 * When fee.type === 'tiered', the country + student-status fields are
 * shown automatically and their answers are sent to the backend as
 * `country`, `is_international`, and `is_student`.
 *
 * ---- MERCH CONFIG (optional add-on, defaults to the AMRS Lacoste shirt) ----
 *   merch = {
 *     enabled: true,
 *     label: 'AMRS Lacoste shirt',
 *     price: 100,
 *     currency: 'GH₵',
 *     sizes: ['S', 'M', 'L', 'XL', 'XXL'],
 *     colors: ['Black', 'White'],
 *   }
 */

const DEFAULT_MERCH = {
  enabled: true,
  label: 'AMRS Lacoste shirt',
  price: 100,
  currency: 'GH₵',
  sizes: ['S', 'M', 'L', 'XL', 'XXL'],
  colors: ['Black', 'White'],
}

const COMMON_COUNTRIES = countryList;

const EventRegisterPage = ({
  backHref,
  registerEndpoint,
  paymentEndpoint,
  mandatoryCourses,
  optionalCourses,
  courseNote,
  fee,
  merch = DEFAULT_MERCH,
}) => {

  const isTiered = fee?.type === 'tiered'

  const [form, setForm] = useState({
        firstName: '',
        surname: '',
        email: '',
        contact: '',
        affiliation: '',
        participation_category: '',
        country: '',
        countryOther: '',
        isStudent: '',
        addMerch : false,
    })

  const [selected, setSelected] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [notification, setNotification] = useState({type: "",  message: ""});
  const storeReference = (reference) => {
            localStorage.setItem('amrsghana_payRef', reference);
        };

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }))}

  const coursesSelected = optionalCourses.filter(course => selected.includes(course.id)).map(course => course.name);
  form.coursesSelected = [...mandatoryCourses.map(c => c.name), ...coursesSelected]

  const toggleCourse = (id) => {
    setSelected((prev) => {
      if (prev.includes(id)) {
        return prev.filter(courseId => courseId !== id);
      }

      return [id];
    });
  };


  const resolvedCountry = form.country === 'Other' ? form.countryOther.trim() : form.country
  const isInternational = isTiered && resolvedCountry !== '' ? resolvedCountry !== 'Ghana' : null
  const isStudentBool = form.isStudent === 'yes' ? true : form.isStudent === 'no' ? false : null

  const matchedTier = useMemo(() => {
    if (!isTiered || isInternational === null || isStudentBool === null) return null
    return fee.tiers.find(t => t.isInternational === isInternational && t.isStudent === isStudentBool) || null
  }, [isTiered, fee, isInternational, isStudentBool])

  const routetoPayment = async () => {
            setIsSubmitting(true);
            try {
                if (form.email) {
                    const PayInitializationResponse = await axios.post(
                        `${BackendUrl}${paymentEndpoint}`,
                        { email: form.email},
                        {
                            headers: {'Accept': 'application/json','Content-Type': 'application/json',},
                            timeout : 10000,
                        },

                    );


                    const { payment_url, reference } = PayInitializationResponse.data;
                    storeReference(reference);

                    setNotification({type: "success", message: "Registration successful! Redirecting to payment..."})
                    setTimeout(() => {window.location.href = payment_url;}, 3000)

                  }

            } catch (err) {
                if (err.response && err.response.status === 301) {
                    const { payment_url, reference } = err.response.data;
                    storeReference(reference);
                    window.location.href = payment_url;
                } else {
                    setSubmissionSuccess(false)
                    setNotification({type: "pending", message: `Registration complete but payment initialization failed. Redirecting....`})
                    setTimeout(() => {window.location.href = `/event/payment-initializer`;}, 10000)
                }
            }finally{
              setIsSubmitting(false);
            }
      }


    const handleSubmit = async (e) => {
        e.preventDefault()
        setIsSubmitting(true);
        setNotification({type : "", message : ""})
        setSubmissionSuccess(false);

        const registrationData = { ...form }
        delete registrationData.countryOther

        if (isTiered) {
          registrationData.country = resolvedCountry
          registrationData.is_international = isInternational
          registrationData.is_student = isStudentBool
        } else {
          delete registrationData.country
          delete registrationData.isStudent
        }

        const dataToSubmit = new FormData();
        dataToSubmit.append("registration_data", JSON.stringify(registrationData));

        try{
            const registrationResponse = await axios.post(
                    `${BackendUrl}${registerEndpoint}`,
                    dataToSubmit,
                    {
                        headers: { 'Accept': '*/*'},
                        timeout: 20000
                    }
                );

              routetoPayment()

            } catch (err) {
              setSubmissionSuccess(false)

                const detail = err.response?.data?.detail;

                if (detail?.eventExpired) {
                  setNotification({type: "error", message: `${detail.message}! Redirecting...`})
                  setTimeout(() => {window.location.href = `${backHref}`;}, 10000)
                }

                if (err.response) {
                    let errorMessage = "Registration failed";

                    if (err.response.data.New_Workshop_Participant_Profile_X == false){
                        setNotification({type : "pending", message : (<>This information has already been recorded. Please proceed to our payment initialize <a href='/event/payment-initializer'>here</a></>)})
                    }


                    else if (err.response.data.detail) {
                        // Handle Pydantic validation errors
                        if (Array.isArray(err.response.data.detail)) {
                            const errors = err.response.data.detail.map(error =>
                                `${error.loc?.join('.')}: ${error.msg}`
                            ).join('; ');
                            errorMessage = `Validation errors: ${errors}`;

                        } else {
                            errorMessage = err.response.data.detail;
                        }
                    } else if (err.response.data.message) {
                        errorMessage = err.response.data.message;

                    } else {setNotification({type: "error", message: errorMessage});}

                } else if (err.request) {
                    setNotification({type: "network error", message: "Request unable to send. Please check your connection and try again."});
                } else {
                    setNotification({type: "error", message: `${err.message}`});
                }


            } finally {
                setIsSubmitting(false);
            }

  }



  return (
    <PageWrapper>
      <PageHeader>
        <Logo onClick={() => window.location.href ='/'}>AMRS</Logo>
        <BackBtn onClick={() => window.location.href = backHref}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5"
            strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back to Event
        </BackBtn>
      </PageHeader>

      {(notification.message) && (
          <Banner type={notification.type}>
            <span>{notification.message}</span>
            <CloseBtn onClick={() => setNotification({ type: "", message: "" })}>
              ✕
            </CloseBtn>
          </Banner>
        )}

      <FormOuter>
        <FormCard onSubmit={handleSubmit}>

          <FeeStrip>
            <FeeLeft>
              <FeeLabel>Registration Fee</FeeLabel>
              {isTiered ? (
                matchedTier ? (
                  <FeeAmount>{matchedTier.currency} {matchedTier.amount.toFixed(2)}</FeeAmount>
                ) : (
                  <FeeAmount $muted>Select country & student status</FeeAmount>
                )
              ) : (
                <FeeAmount>{fee.currency} {fee.amount.toFixed(2)}</FeeAmount>
              )}
              <FeeNote>{fee.note}{fee.deadline ? ` · Register by ${fee.deadline}` : ''}</FeeNote>
            </FeeLeft>
            <FeeRight>Redirects to Payment Service Provider (PSP) after submitting this form.</FeeRight>
          </FeeStrip>

          <SectionTitle>
            <UserIcon />
            Personal Information
          </SectionTitle>

          <FormGrid>
            <Field>
              <label>First name <Req>*</Req></label>
              <input name="firstName" value={form.firstName}
                onChange={handleChange} placeholder="e.g. Kwame" required />
            </Field>
            <Field>
              <label>Surname <Req>*</Req></label>
              <input name="surname" value={form.surname}
                onChange={handleChange} placeholder="e.g. Mensah" required />
            </Field>
            <Field>
              <label>Email address <Req>*</Req></label>
              <input type="email" name="email" value={form.email}
                onChange={handleChange} placeholder="you@example.com" required />
            </Field>
            <Field>
              <label>Contact / Phone <Req>*</Req></label>
              <input type="tel" name="contact" value={form.contact}
                onChange={handleChange} placeholder="+233 XX XXX XXXX" required />
            </Field>
            <Field className="full">
              <label>Affiliation <Req>*</Req></label>
              <input name="affiliation" value={form.affiliation}
                onChange={handleChange}
                placeholder="University, institution, or organisation" required />
            </Field>
            <Field className="full">
              <label>Participant category <Req>*</Req></label>
              <select name="participation_category" value={form.participation_category}
                onChange={handleChange} >
                <option value="" disabled>Select your category</option>
                <option>Undergraduate Student</option>
                <option>Postgraduate Student</option>
                <option>Researcher / Academic</option>
                <option>Industry Professional</option>
                <option>Other</option>
              </select>
            </Field>

            {isTiered && (
              <>
                <Field>
                  <label>Country <Req>*</Req></label>
                  <select name="country" value={form.country} onChange={handleChange} required>
                    <option value="" disabled>Select your country</option>
                    {COMMON_COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </Field>
                {form.country === 'Other' && (
                  <Field>
                    <label>Please specify <Req>*</Req></label>
                    <input name="countryOther" value={form.countryOther}
                      onChange={handleChange} placeholder="Your country" required />
                  </Field>
                )}
                <Field>
                  <label>Are you a student? <Req>*</Req></label>
                  <RadioRow>
                    {['yes', 'no'].map(v => (
                      <label key={v}>
                        <input type="radio" name="isStudent" value={v}
                          checked={form.isStudent === v}
                          onChange={handleChange} required />
                        {v === 'yes' ? 'Yes' : 'No'}
                      </label>
                    ))}
                  </RadioRow>
                </Field>
              </>
            )}
          </FormGrid>

          <Divider />

          <SectionTitle>
            <BookIcon />
            Course Selection
          </SectionTitle>

          {courseNote && (
            <FormNote>{courseNote}</FormNote>
          )}

          <CoursesGrid>
            {[...mandatoryCourses, ...optionalCourses].map(course => {
              const checked = course.mandatory || selected.includes(course.id)
              return (
                <CourseOption key={course.id}>
                  <input type="checkbox" id={course.id}
                    checked={checked}
                    disabled={course.mandatory}
                    onChange={() => !course.mandatory && toggleCourse(course.id)}
                  />
                  <CourseLabel htmlFor={course.id} $checked={checked}>
                    <CourseCheck $checked={checked}>
                      {checked && (
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
                          stroke="#fff" strokeWidth="3"
                          strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </CourseCheck>
                    <CourseInfo>
                      <CourseName>{course.name}</CourseName>
                      <CourseTag $mandatory={course.mandatory}>
                        {course.mandatory ? course.tag : 'Optional'}
                      </CourseTag>
                    </CourseInfo>
                  </CourseLabel>
                </CourseOption>
              )
            })}
          </CoursesGrid>

          {merch?.enabled && (
            <>
              <Divider />

              <SectionTitle>
                <PlusIcon />
                Optional purchase
              </SectionTitle>

              <FormNote>
                <p><strong>{merch.label}</strong> {merch.label.toLowerCase().includes('are') ? '' : 'is'} available for purchase for an additional {merch.currency} {merch.price}.
                  You may check to add this to your package and select your preferred color and size.</p>
              </FormNote>

              <OptionalBox>
                  <LacosteOption>
                    <HiddenCheckbox
                          checked={form.addMerch}
                          onChange={(e) => setForm(prev => ({...prev, addMerch: e.target.checked, lacosteSize: e.target.checked ? prev.lacosteSize : "none",
                                    lacosteColor: e.target.checked ? prev.lacosteColor : "none"}))}

                      />
                  <StyledCheckbox />
                  <LabelText>Add {merch.label}</LabelText>
                </LacosteOption>


                  {form.addMerch && (
                    <Preference>

                      <div>
                        <p>Select Size:</p>
                        {merch.sizes.map(size => (
                          <label key={size} style={{ marginRight: "10px"}}>
                            <input
                              type="radio"
                              name="lacosteSize"
                              value={size}
                              checked={form.lacosteSize === size}
                              required
                              onChange={(e) =>
                                setForm(prev => ({
                                  ...prev,
                                  lacosteSize: e.target.value
                                }))
                              }
                            />
                            {size}
                          </label>
                        ))}
                      </div>

                      <div>
                        <p>Select Color:</p>
                        {merch.colors.map(color => (
                          <label key={color} style={{ marginRight: "10px"}}>
                            <input
                              type="radio"
                              name="lacosteColor"
                              value={color}
                              checked={form.lacosteColor === color}
                              required
                              onChange={(e) =>
                                setForm(prev => ({
                                  ...prev,
                                  lacosteColor: e.target.value
                                }))
                              }
                            />
                            {color}
                          </label>
                        ))}
                      </div>

                    </Preference>
                  )}

              </OptionalBox>
            </>
          )}

          <Divider />

          <SubmitRow>
            <SubmitNote>
              By submitting you agree to AMRS Ghana's terms. You will be redirected
              to complete payment to confirm your spot.
            </SubmitNote>
            <SubmitBtn type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : "Submit"}
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2.5"
                strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </SubmitBtn>
          </SubmitRow>

        </FormCard>
      </FormOuter>
    </PageWrapper>
  )
}



const PageWrapper = styled.div`background: #f5f3ef; min-height: 100vh;`

const PageHeader = styled.header`
  background: #0f1923;
  padding: 14px 2.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const Logo = styled.div`
  color: #fff; font-size: 1.3rem; font-weight: 700; letter-spacing: 0.5px;
`

const BackBtn = styled.button`
  display: inline-flex; align-items: center; gap: 7px;
  background: transparent; border: 1px solid rgba(255,255,255,0.25);
  color: rgba(255,255,255,0.8); font-size: 12px; font-weight: 500;
  letter-spacing: 0.5px; padding: 7px 16px; cursor: pointer;
  border-radius: 6px; transition: all 0.2s;
  &:hover { border-color: #c8870a; color: #c8870a; }
`

const Banner = styled.div`
  margin : 1rem auto;
  width: 90%;
  padding: 14px 18px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;

  display: flex;
  justify-content: space-between;
  align-items: center;

  color: ${({ type }) => (type === "success" ? "#065f46" 
    :type === "pending" ? "#a74e17":"#7f1d1d")};
  background: ${({ type }) => (type === "success" ? "#d1fae5" 
    : type === "pending"? "#fef3c7" : "#fee2e2")};
  border: 1px solid
    ${({ type }) => (type === "success" ? "#10b981" 
      :type === "pending" ? "#f59e0b" : "#ef4444")};
`;

const CloseBtn = styled.button`
  background: transparent;
  border: none;
  font-size: 16px;
  cursor: pointer;
  color: inherit;
`;

const FormOuter = styled.div`
  max-width: 820px; margin: -10px auto 0;
  padding: 0 1rem 4rem;
  position: relative; z-index: 2;
`

const FormCard = styled.form`
  background: #fff; border-radius: 14px;
  margin-top : 4rem;
  border: 0.5px solid rgba(0,0,0,0.08);
  padding: 2.5rem 2.5rem 2rem;
  @media (max-width: 640px) { padding: 1.75rem 1.25rem; }
`

const FeeStrip = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  background: #0f1923; border-radius: 10px; padding: 16px 20px;
  margin-bottom: 2rem; flex-wrap: wrap; gap: 10px;
`

const FeeLeft = styled.div`display: flex; flex-direction: column; gap: 3px;`
const FeeLabel = styled.span`font-size: 10px; font-weight: 600; letter-spacing: 1.5px; text-transform: uppercase; color: rgba(255,255,255,0.4);`
const FeeAmount = styled.span`font-size: ${p => p.$muted ? '15px' : '22px'}; font-weight: 700; color: ${p => p.$muted ? 'rgba(255,255,255,0.5)' : '#fff'};`
const FeeNote = styled.span`font-size: 11px; color: rgba(255,255,255,0.4);`
const FeeRight = styled.p`font-size: 12px; color: rgba(255,255,255,0.55); text-align: right; line-height: 1.6;`

const SectionTitle = styled.h3`
  font-size: 11px; font-weight: 600; letter-spacing: 2px;
  text-transform: uppercase; color: #0c4e80;
  margin-bottom: 1.25rem; padding-bottom: 10px;
  border-bottom: 0.5px solid rgba(0,0,0,0.07);
  display: flex; align-items: center; gap: 8px;
`

const FormGrid = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 18px 24px;
  margin-bottom: 2rem;
  .full { grid-column: 1 / -1; }
  select, input  {
    height: 42px; padding: 0 14px;
    border: 1px solid #e5e7eb; border-radius: 8px;
    font-size: 14px; color: #111;
    outline: none; transition: border-color 0.2s, box-shadow 0.2s;
    &:focus { border-color: #0c4e80; box-shadow: 0 0 0 3px rgba(12,78,128,0.08); }
  }
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`

const Field = styled.div`
  display: flex; flex-direction: column; gap: 6px;
  label { font-size: 12px; font-weight: 600; color: #374151; }
`

const RadioRow = styled.div`
  display: flex; align-items: center; gap: 18px; height: 42px;
  label { display: flex; align-items: center; gap: 6px; font-size: 14px; font-weight: 500; color: #111; }
`

const Req = styled.span`color: #c8870a; margin-left: 2px;`

const Divider = styled.div`
  height: 0.5px; background: rgba(0,0,0,0.07); margin: 0 0 1.75rem;
`

const FormNote = styled.div`
  display: flex; align-items: flex-start; gap: 10px;
  background: #faf3e8; border: 0.5px solid #fae5c2;
  border-radius: 8px; padding: 2px 14px; margin-bottom: 1.75rem;
  p { font-size: 10px; color: #633806; line-height: 1.55; }
  strong { font-weight: 600; }
`

const CoursesGrid = styled.div`
  display: grid; grid-template-columns: 1fr 1fr;
  gap: 10px; margin-bottom: 2rem;
  @media (max-width: 640px) { grid-template-columns: 1fr; }
`

const CourseOption = styled.div`
  input { display: none; }
`

const CourseLabel = styled.label`
  display: flex; align-items: flex-start; gap: 11px;
  padding: 12px 14px; border-radius: 9px; cursor: pointer;
  border: 1px solid ${p => p.$checked ? '#0c4e80' : '#e5e7eb'};
  background: ${p => p.$checked ? '#E6F1FB' : '#fafafa'};
  box-shadow: ${p => p.$checked ? '0 0 0 3px rgba(12,78,128,0.07)' : 'none'};
  transition: all 0.2s;
`

const CourseCheck = styled.div`
  width: 18px; height: 18px; flex-shrink: 0; margin-top: 1px;
  border: 1.5px solid ${p => p.$checked ? '#0c4e80' : '#d1d5db'};
  border-radius: 4px; background: ${p => p.$checked ? '#0c4e80' : '#fff'};
  display: flex; align-items: center; justify-content: center;
  transition: all 0.2s;
`

const CourseInfo = styled.div`flex: 1; min-width: 0;`
const CourseName = styled.div`font-size: 13px; font-weight: 600; color: #111; line-height: 1.3;`

const CourseTag = styled.span`
  display: inline-block; margin-top: 4px;
  font-size: 10px; font-weight: 500; padding: 2px 8px; border-radius: 999px;
  background: ${p => p.$mandatory ? '#FAEEDA' : '#f3f4f6'};
  color: ${p => p.$mandatory ? '#633806' : '#6b7280'};
  border: 0.5px solid ${p => p.$mandatory ? '#FAC775' : '#e5e7eb'};
`

const OptionalBox =styled.div`
  margin-bottom : 2rem;
`

const LacosteOption = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: 14px;
  color: #111;
`;

const HiddenCheckbox = styled.input.attrs({ type: "checkbox" })`
  display: none;
`;

const LabelText = styled.span`
  font-weight: 500;
`

const StyledCheckbox = styled.span`
  width: 18px;
  height: 18px;
  border: 1.5px solid ${p => p.$checked ? '#0c4e80' : '#d1d5db'};
  border-radius: 4px; background: ${p => p.$checked ? '#0c4e80' : '#fff'};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  flex-shrink : 0;

  ${HiddenCheckbox}:checked + & {
    background-color: #0c4e80;

  }

  &::after {
    content: "";
    width: 3px;
    height: 7px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
    display: none;
  }

  ${HiddenCheckbox}:checked + &::after {
    display: block;
  }
`

const Preference = styled.div`
  display : flex;
  gap : 30%;
  font-size: 14px; font-weight: 600;
  color: #111; line-height: 1.3; margin: 10px 0; padding-left: 15px;

  @media (max-width: 640px){display: block}
`

const SubmitRow = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  flex-wrap: wrap; gap: 14px; padding-top: 4px;
`

const SubmitNote = styled.p`
  font-size: 12px; color: #9ca3af; line-height: 1.5; max-width: 360px;
`

const SubmitBtn = styled.button`
  display: inline-flex; align-items: center; gap: 9px;
  background: #c8870a; color: #fff; border: none;
  padding: 13px 28px; font-size: 13px; font-weight: 600;
  letter-spacing: 0.8px; text-transform: uppercase;
  border-radius: 8px; cursor: pointer; transition: background 0.2s;
  &:hover { background: #a86e08; }
`


const UserIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
  </svg>
)
const BookIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
  </svg>
)

const PlusIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }} >
    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="16" /><line x1="8" y1="12" x2="16" y2="12" />
  </svg>
)

export default EventRegisterPage;
